import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Alert, AlertTitle,
  Accordion, AccordionSummary, AccordionDetails, Link, Paper,
  CircularProgress, Tooltip, ButtonGroup,
} from '@mui/material';
import { Warning, Info, ExpandMore, Launch } from '@mui/icons-material';
import Plot from 'react-plotly.js';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';

// Services & Contexts
import { getMooringCleanedRollups, getMooringsByGridId } from '../services/mooringService';
import { useAuth } from '../contexts/AuthContext';
import SafetyLevelsDialog from '../components/SafetyLevelsDialog';
import '../styles/GridDetailPage.css';

// ==========================================
// 1. CONSTANTS & CONFIG
// ==========================================

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const CALM_WIND_THRESHOLD_MS = 0.5;

const TIME_RANGES = [
  { key: '24h', label: '24h', ms: 24 * 60 * 60 * 1000 },
  { key: '3d',  label: '3d',  ms: 3 * 24 * 60 * 60 * 1000 },
  { key: '1w',  label: '1w',  ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '2w',  label: '2w',  ms: 14 * 24 * 60 * 60 * 1000 },
  { key: '1m',  label: '1m',  ms: 30 * 24 * 60 * 60 * 1000 },
  { key: 'launch', label: 'Since Launch', ms: null },
];

const SENSOR_TYPES = {
  conductivity: { name: 'Conductivity', unit: 'uS/cm', color: '#7b1fa2' },
  o2_25: { name: 'Dissolved Oxygen - 25', unit: 'mg/L', color: '#7b1fa2' },
  o2: { name: 'Dissolved Oxygen', unit: 'mg/L', color: '#7b1fa2' },
  ph_25: { name: 'pH - 25', unit: 'μg/L', color: '#1976d2' },
  ph: { name: 'pH', unit: 'mg/L', color: '#7b1fa2' },
  chla: { name: 'Chlorophyll-a', unit: 'μg/L', color: '#388e3c' },
  turbidity: { name: 'Turbidity', unit: 'NTU', color: '#b71c1c' },
  cdom: { name: 'CDOM', unit: 'ppb', color: '#388e3c' },
  temperature: { name: 'Temperature', unit: '°F', color: '#f57c00' },
  humidity_o2: {name: 'O2 Humidity', unit: '%', color:'#43a047' },
  battery_voltage_calibrated: {name: 'Battery Voltage Calib.', unit: 'PCB', color: '#c6362eff'},
  par: {name: 'PAR', unit: 'PCB', color: '#c6362eff'},
  pump_speed: {name: 'Pump Speed', unit: 'RPM', color: '#c6362eff'},
  wave_height: { name: 'Wave Height', unit: 'cm', color: '#8e24aa' },
  wave_direction: { name: 'Wave Direction', unit: '°', color: '#fb8c00' },
  wave_direction_true: { name: 'Wave Direction (True)', unit: '°', color: '#ef6c00' },
  wind_speed: { name: 'Wind Speed', unit: 'm/s', color: '#1e88e5' },
  wind_direction_true: { name: 'Wind Direction (True)', unit: '°', color: '#43a047' },
  wind_speed_ext: { name: 'Wind Speed (Ext)', unit: 'm/s', color: '#1565c0' },
  wind_direction_ext: { name: 'Wind Direction (Ext)', unit: '°', color: '#2e7d32' },
  precip_accum_1hr_ext: { name: 'Precip Accum 1h (Ext)', unit: 'mm', color: '#546e7a' },
  precip_accum_24hr_ext: { name: 'Precip Accum 24h (Ext)', unit: 'mm', color: '#263238' },
  air_temperature_ext: { name: 'Air Temperature (Ext)', unit: 'Celsius', color: '#c6362eff' },
};

const MET_CHOICES = [
    'wind_speed', 'wind_direction_true',
    'wind_speed_ext', 'wind_direction_ext',
    'precip_accum_1hr_ext', 'precip_accum_24hr_ext','air_temperature_ext'
  ];

  const ROSE_TICKS = {
    vals: [0, 45, 90, 135, 180, 225, 270, 315],
    text: ['N','NE','E','SE','S','SW','W','NW']
  };

  // ==========================================
// 2. STATIC UTILITY & MATH FUNCTIONS
// ==========================================

const formatDateTime = (isoString) => {
    if (!isoString) return 'Unknown';
    return new Date(isoString).toLocaleString('en-US', {
      timeZone: 'US/Eastern', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getStatusDotColor = (lastUpdate) => {
    if (!lastUpdate) return '#f44336';
    const hoursDiff = (new Date() - new Date(lastUpdate)) / (1000 * 60 * 60);
    if (hoursDiff < 2) return '#4caf50';
    if (hoursDiff < 6) return '#ff9800';
    return '#f44336';
  };

  const mpsToMph = (m) => (Number.isFinite(m) ? m * 2.23693629 : m);

// WIND MATH HELPERS (Restored for "Prevailing" calculation)
const mod360 = (d) => ((d % 360) + 360) % 360;
const circDist = (a, b) => {
    const diff = Math.abs(mod360(a) - mod360(b));
    return Math.min(diff, 360 - diff);
};
const circularMedianDeg = (anglesDeg = []) => {
    const vals = anglesDeg.map(Number).filter(Number.isFinite);
    if (!vals.length) return null;
    let best = vals[0], bestCost = Infinity;
    for (const cand of vals) {
        const cost = vals.reduce((s, v) => s + circDist(cand, v), 0);
        if (cost < bestCost) { bestCost = cost; best = cand; }
    }
    return mod360(best);
};
const median = (nums = []) => {
    const arr = nums.map(Number).filter(Number.isFinite).sort((a,b)=>a-b);
    if (!arr.length) return null;
    const m = Math.floor(arr.length / 2);
    return arr.length % 2 ? arr[m] : (arr[m-1] + arr[m]) / 2;
};
const degToCompass = (degFrom) => {
    const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
    const idx = Math.round(mod360(degFrom) / 22.5) % 16;
    return dirs[idx];
};

const bucketMinutesForRange = (key) => {
  switch (key) {
    case '24h': return 4;
    case '3d':  return 16;
    case '1w':  return 30;
    case '2w':  return 60;
    case '1m':  return 120;
    case 'launch': return 240; // 1 day intervals to prevent massive 16MB payloads
    default:    return 60;
  }
};

const computeArrowResolution = (msRange) => {
  const oneDay = 24 * 60 * 60 * 1000;
  if (msRange < oneDay) return 15;
  if (msRange < 3 * oneDay) return 30;
  return 60;
};

const transformSensorData = (sensorData) => {
    if (!Array.isArray(sensorData) || sensorData.length === 0) return {};
    const groupedData = sensorData.reduce((acc, reading) => {
      if (!reading || !reading.sensor_kind || reading.time === undefined || reading.sensor_value === undefined) return acc;
      const { sensor_kind, time, unit, sensor_value, depth } = reading;
      if (!acc[sensor_kind]) acc[sensor_kind] = [];
      acc[sensor_kind].push({ time, unit, sensor_value, depth });
      return acc;
    }, {});
    Object.keys(groupedData).forEach(k => groupedData[k].sort((a, b) => new Date(b.time) - new Date(a.time)));
    return groupedData;
  };

  const pickLatestLatLon = (arr) => {
    if (!Array.isArray(arr) || !arr.length) return null;
    const latest = [...arr]
      .filter(r => Number.isFinite(r?.latitude) && Number.isFinite(r?.longitude))
      .sort((a, b) => new Date(b.time) - new Date(a.time))[0];
    return latest ? { lat: latest.latitude, lon: latest.longitude, t: latest.time } : null;
  };

  const pickLatestLatLonFromPayload = (payload) => {
    if (!Array.isArray(payload) || !payload.length) return null;
    const waveDir = payload.filter(r => r?.sensor_kind === 'wave_direction');
    const bestWave = pickLatestLatLon(waveDir);
    if (bestWave) return bestWave;
    return pickLatestLatLon(payload);
  };

  // --- HEAVY VISUALIZATION FUNCTIONS ---

const buildCompassRose = (corner = 'tr') => {
    const padX = 0.035, padY = 0.04;
    const cx = corner === 'tr' ? 1 - padX : padX;
    const cy = 1 - padY;
    const r = 0.055;
    const shapes = [
      { type: 'circle', xref: 'paper', yref: 'paper', x0: cx - r, x1: cx + r, y0: cy - r, y1: cy + r, line: { color: 'rgba(0,0,0,0.45)', width: 1 }, fillcolor: 'rgba(255,255,255,0.65)', layer: 'above' },
      { type: 'line', xref: 'paper', yref: 'paper', x0: cx, x1: cx, y0: cy - r, y1: cy + r, line: { color: 'rgba(0,0,0,0.35)', width: 1 } },
      { type: 'line', xref: 'paper', yref: 'paper', x0: cx - r, x1: cx + r, y0: cy, y1: cy, line: { color: 'rgba(0,0,0,0.35)', width: 1 } },
    ];
    const ann = (x, y, text, xanchor = 'center', yanchor = 'middle') => ({ xref: 'paper', yref: 'paper', x, y, text, showarrow: false, xanchor, yanchor, font: { size: 11, color: '#333' }, bgcolor: 'rgba(0,0,0,0)' });
    const d = r + 0.018;
    const annotations = [
      ann(cx, cy + d, 'N', 'center', 'bottom'), ann(cx + d, cy, 'E', 'left', 'middle'),
      ann(cx, cy - d, 'S', 'center', 'top'), ann(cx - d, cy, 'W', 'right', 'middle'),
    ];
    return { shapes, annotations };
  };

  const buildDirectionRose = (transformedData, { dirKind, weightKind = null, nBins = 16 }) => {
    const dirs = transformedData[dirKind] || [];
    const mags = weightKind ? (transformedData[weightKind] || []) : null;
    if (!dirs.length) return { theta: [], r: [] };

    let magMap = null;
    if (mags && mags.length) {
      magMap = new Map(mags.map(m => [new Date(m.time).getTime(), Number(m.sensor_value)]));
    }

    const binSize = 360 / nBins;
    const bins = new Array(nBins).fill(0);
    for (const d of dirs) {
      const val = Number(d.sensor_value);
      if (!Number.isFinite(val)) continue;
      let deg = ((val % 360) + 360) % 360;
      const idx = Math.min(nBins - 1, Math.floor(deg / binSize));
      const t = new Date(d.time).getTime();
      const w = (magMap && Number.isFinite(magMap.get(t))) ? Math.max(0, magMap.get(t)) : 1;
      bins[idx] += w;
    }
    const theta = bins.map((_, i) => (i * binSize) + binSize / 2);
    return { theta, r: bins };
  };

  const buildArrowBaselineTimeSeries = (transformedData, dirKind, magKind, {
      dirAliases = [], resolutionMin = 60, applyCalm = false, calmThreshold = CALM_WIND_THRESHOLD_MS, arrowCap = 48, calmDot = { size: 5 }
    } = {}) => {
      const pickDir = (k) => (transformedData[k] || []);
      let dirs = pickDir(dirKind);
      if (!dirs.length) {
        for (const alt of dirAliases) {
          if (pickDir(alt).length) { dirs = pickDir(alt); break; }
        }
      }
      const mags = transformedData[magKind] || [];
      if (!dirs.length) return { traces: [], annotations: [], meanAnnots: [], meanShapes: [], baselineShape: null };

      const bucketMs = resolutionMin * 60 * 1000;
      const floorBucket = (d) => Math.floor(new Date(d).getTime() / bucketMs) * bucketMs;

      const dirByBucket = new Map();
      const magByBucket = new Map();

      for (const d of dirs) {
        const ts = floorBucket(d.time);
        const toDeg = (Number(d.sensor_value) + 180) % 360;
        if (!Number.isFinite(toDeg)) continue;
        if (!dirByBucket.has(ts)) dirByBucket.set(ts, []);
        dirByBucket.get(ts).push(toDeg);
      }

      for (const m of mags) {
        const ts = floorBucket(m.time);
        const v = Number(m.sensor_value);
        if (!Number.isFinite(v)) continue;
        if (!magByBucket.has(ts)) magByBucket.set(ts, []);
        magByBucket.get(ts).push(v);
      }

      const buckets = Array.from(dirByBucket.keys()).sort((a, b) => a - b);
      const xs = [], dirsTo = [], magsAvgM = [];

      for (const b of buckets) {
        let sx = 0, sy = 0;
        for (const deg of dirByBucket.get(b)) {
          const r = (deg * Math.PI) / 180;
          sx += Math.cos(r); sy += Math.sin(r);
        }
        const meanDeg = ((Math.atan2(sy, sx) * 180) / Math.PI + 360) % 360;
        const mList = magByBucket.get(b) || [];
        const mAvg = mList.length ? (mList.reduce((a, c) => a + c, 0) / mList.length) : NaN;
        xs.push(new Date(b));
        dirsTo.push(meanDeg);
        magsAvgM.push(mAvg);
      }
      if (!xs.length) return { traces: [], annotations: [], meanAnnots: [], meanShapes: [], baselineShape: null };

      const valids = magsAvgM.filter(Number.isFinite);
      const p90 = valids.length ? valids.sort((a,b)=>a-b)[Math.floor(0.9 * (valids.length-1))] : 1;
      const scaleMax = Math.max(p90, 1e-6);
      const pxLen = (mMeters) => {
          if (!Number.isFinite(mMeters)) return 18;
          return 18 + (28 - 18) * Math.cbrt(Math.max(0, Math.min(mMeters, scaleMax)) / scaleMax);
      };

      const annotations = [];
      const calmXs = [], calmHover = [], headXs = [], headYs = [], headHover = [];
      const stride = Math.max(1, Math.ceil(xs.length / arrowCap));

      for (let i = 0; i < xs.length; i += stride) {
        const x = xs[i], m = magsAvgM[i], dir = dirsTo[i];
        const dispMag = Number.isFinite(m) ? (magKind === 'wave_height' ? (m*100).toFixed(1) : m.toFixed(2)) : '—';
        const dispUnit = magKind === 'wave_height' ? 'cm' : (SENSOR_TYPES[magKind]?.unit || '');

        if (applyCalm && (!Number.isFinite(m) || m < calmThreshold)) {
          calmXs.push(x);
          calmHover.push(`${SENSOR_TYPES[dirKind]?.name}: calm<br>${SENSOR_TYPES[magKind]?.name}: ${dispMag} ${dispUnit}`);
          continue;
        }
        const theta = (dir * Math.PI) / 180;
        const L = pxLen(m);
  annotations.push({
      x, xref: 'x',
      y: 1, yref: 'y', // Tail (anchor) at baseline
      axref: 'pixel', ayref: 'pixel',
      // Calculate offset for the HEAD (Tip). Pixel offsets are screen coords
      // (+y is DOWN), so a bearing clockwise from north maps to (sin, -cos).
      ax: Math.sin(theta) * L,
      ay: -Math.cos(theta) * L,
      showarrow: true,
      arrowhead: 2,    // Style of the arrow head
      arrowsize: 1.4,
      arrowwidth: 2,
      opacity: 0.95,
      arrowcolor: SENSOR_TYPES[dirKind]?.color || '#444',
      // Logic: Draw arrow from (x+ax, y+ay) points TO (x,y).
      // If we want the HEAD at (x+ax, y+ay) and TAIL at (x,y), we inverse the logic slightly or use 'end'.
      // Standard Plotly: 'arrowside: end' puts arrowhead at (x,y). We want arrowhead at the OFFSET.
      // So we use 'start' to put head at start (ax,ay) and nothing at end (x,y).
      arrowside: 'start',
      startarrowhead: 2, // Put the head at the start (the offset tip)
  });
        headXs.push(x); headYs.push(1);
        headHover.push(`${SENSOR_TYPES[dirKind]?.name}: ${dir.toFixed(0)}° (TO)<br>${SENSOR_TYPES[magKind]?.name}: ${dispMag} ${dispUnit}`);
      }

      return {
        traces: [
          { x: xs, y: Array(xs.length).fill(1), type: 'scatter', mode: 'lines', line: { width: 0 }, hoverinfo: 'skip', showlegend: false, opacity: 0 },
          calmXs.length ? { x: calmXs, y: Array(calmXs.length).fill(1), type: 'scatter', mode: 'markers', marker: { symbol: 'circle', size: calmDot.size, color: 'rgba(0,0,0,0.45)' }, hovertemplate: '%{text}<extra></extra>', text: calmHover, showlegend: false } : null,
          headXs.length ? { x: headXs, y: headYs, type: 'scatter', mode: 'markers', marker: { size: 14, opacity: 0.001 }, hovertemplate: '%{text}<extra></extra>', text: headHover, showlegend: false } : null
        ].filter(Boolean),
        annotations,
        meanAnnots: [], meanShapes: [],
        baselineShape: { type: 'line', xref: 'x', yref: 'y', x0: xs[0], x1: xs[xs.length-1], y0: 1, y1: 1, line: { width: 0.8, color: 'rgba(0,0,0,0.25)', dash: 'dot' }, layer: 'below' }
      };
  };

  const generateChartData = (data, sensorType) => {
      if (!data || !data[sensorType] || data[sensorType].length === 0) return [];
      const chron = [...data[sensorType]].reverse();

      if (sensorType === 'cyanobacteria_index') {
        return chron.map(item => ({
          time: item.time, // Passing raw ISO time for ticks
          value: item.sensor_value || 0,
          risk: (item.sensor_value >= 3) ? 'high' : (item.sensor_value >= 2 ? 'medium' : (item.sensor_value >= 1 ? 'low' : 'undetectable'))
        }));
      }
      if (sensorType === 'wave_height') {
        return chron.map(item => ({ x: new Date(item.time), y: (item.sensor_value || 0) * 100.0 }));
      }
      return chron.map(item => ({
        x: new Date(item.time),
        y: item.sensor_value || 0,
        depth: item.depth
      }));
  };

  const generatePlotData = (chartData, sensorType) => {
      if (sensorType === 'cyanobacteria_index') {
          return [{
              x: chartData.map(d => d.time),
              y: chartData.map(d => {
                  if (d.value >= 3) return 4;
                  if (d.value >= 2) return 3;
                  if (d.value >= 1) return 2;
                  return 1;
              }),
              type: 'bar',
              marker: {
                  color: chartData.map(d => {
                      if (d.value >= 3) return 'rgba(244, 67, 54, 0.7)';
                      if (d.value >= 2) return 'rgba(255, 152, 0, 0.7)';
                      if (d.value >= 1) return 'rgba(255, 193, 7, 0.7)';
                      return 'rgba(76, 175, 80, 0.7)';
                  })
              },
              // CHANGE THIS LINE: Added %H:%M to show timestamp on hover
              hovertemplate: `%{x|%b %d %H:%M}: Risk Index %{customdata}<extra></extra>`,
              customdata: chartData.map(d => d.value.toFixed(1))
          }];
      }


      // Line Charts
      const depth1 = chartData.filter(d => d.depth === 1 || d.depth === 1.0);
      const depth2 = chartData.filter(d => d.depth === 2 || d.depth === 2.0);
      const traces = [];

      const processY = (y) => (sensorType === 'temperature' ? (y * 9/5 + 32) : (sensorType === 'o2_25' ? y * 0.032 : y));
      const colors = {
        'temperature': { d1: '#d32f2f', d2: '#1976d2' },
        'conductivity': { d1: '#5e35b1', d2: '#43a047' },
        'o2_25': { d1: '#6a1b9a', d2: '#26a69a' },
        'o2': { d1: '#6a1b9a', d2: '#26a69a' },
        'ph_25': {d1: '#1976d2',d2: '#26a69a'  },
        'ph': {d1: '#1976d2',d2: '#26a69a'  },
        'turbidity': { d1: '#1976d2', d2: '#ff6b6b' },
        'cdom': { d1: '#1976d2', d2: '#ff6b6b' },
        'chla': { d1: '#2e7d32', d2: '#ff9800' },
        'humidity_o2': { d1: '#2e7d32', d2: '#ff9800' },
        'battery_voltage_calibrated': { d1: '#2e7d32', d2: '#ff9800' },
        'par': { d1: '#2e7d32', d2: '#ff9800' },
        'pump_speed': { d1: '#2e7d32', d2: '#ff9800' },
      }[sensorType] || { d1: '#1976d2', d2: '#ff6b6b' };

      const addTrace = (data, name, color) => {
          if (!data.length) return;
          traces.push({
              x: data.map(d => d.x),
              y: data.map(d => processY(d.y)),
              type: 'scatter', mode: 'lines', name,
              line: { color, width: 2.5 }, marker: { color, size: 6 },
              hovertemplate: `${name}<br>%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${SENSOR_TYPES[sensorType]?.unit}<extra></extra>`
          });
      };

      addTrace(depth1, 'Surface', colors.d1);
      addTrace(depth2, 'Bottom', colors.d2);

      // No depth distinction (flat data)
      if (!depth1.length && !depth2.length && chartData.length > 0) {
          const isPoint = sensorType.includes('wind_speed');
          traces.push({
              x: chartData.map(d => d.x),
              y: chartData.map(d => processY(d.y)),
              type: 'scatter',
              mode: isPoint ? 'markers' : 'lines',
              line: isPoint ? undefined : { color: SENSOR_TYPES[sensorType]?.color, width: 2 },
              marker: { color: SENSOR_TYPES[sensorType]?.color, size: 6 },
              name: SENSOR_TYPES[sensorType]?.name,
              hovertemplate: `%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${SENSOR_TYPES[sensorType]?.unit}<extra></extra>`
          });
      }
      return traces;
  };

  // ==========================================
// 3. MAIN COMPONENT
// ==========================================

export default function MarinePage() {
    const { 'grid-id': gridId, 'mooring-id': mooringId } = useParams();
    const { grids, gridsLoading, token, user } = useAuth();
    const isAdmin = !!(user?.is_admin);

    // --- STATE ---
    const [rangeKey, setRangeKey] = useState('3d');
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [error, setError] = useState(null);
    const [selectedTimeseriesType, setSelectedTimeseriesType] = useState('temperature');
    const [selectedEnvType, setSelectedEnvType] = useState('wave_height');
    const [selectedMetType, setSelectedMetType] = useState('wind_speed_ext');
    const [latestSync, setLatestSync] = useState(null);
    const [arrowResolutionMin, setArrowResolutionMin] = useState(bucketMinutesForRange('3d'));
    const [windRoseSource, setWindRoseSource] = useState('internal');
    const [windCardSource, setWindCardSource] = useState('internal');
    const [safetyDialogOpen, setSafetyDialogOpen] = useState(false);
    const [launchDate, setLaunchDate] = useState(null);


    // Data State
    const [waterQualityData, setWaterQualityData] = useState([]);
    const [oneMonthCache, setOneMonthCache] = useState(null);
    const [loading, setLoading] = useState(true);
    const [prefetching, setPrefetching] = useState(false);
    const [loadingPriority2, setLoadingPriority2] = useState(false);
    const [loadingPriority3, setLoadingPriority3] = useState(false);
    const [latestLatLon, setLatestLatLon] = useState(null);
    const [mooring, setMooring] = useState(null);

    // Google Maps - UPDATED with correct ID for script
    const { isLoaded: mapLoaded } = useLoadScript({
      googleMapsApiKey: GOOGLE_MAPS_API_KEY,
      id: 'pondpage-maps-script'
    });

    const gridData = grids.find(grid => grid.id === parseInt(gridId));
    const rangeKeyRef = useRef(rangeKey);

  // --- MEMOIZED DATE RANGE ---
  const { startDateISO, endDateISO, rangeLabel } = useMemo(() => {
    const end = new Date();

    if (rangeKey === 'launch') {
      // 1. Safe date parsing
      let start = new Date('2025-01-01T00:00:00Z'); // Default fallback

      if (launchDate) {
          const parsedLaunch = new Date(launchDate);
          // Only use launchDate if it is a valid date
          if (!isNaN(parsedLaunch.getTime())) {
              start = parsedLaunch;
          }
      }

      return {
          startDateISO: start.toISOString(),
          endDateISO: end.toISOString(),
          rangeLabel: 'Since Launch'
      };
    }

    const preset = TIME_RANGES.find(r => r.key === rangeKey) || TIME_RANGES.find(r => r.key === '3d');
    const start = new Date(end.getTime() - (preset?.ms ?? 3 * 24 * 60 * 60 * 1000));

    return {
        startDateISO: start.toISOString(),
        endDateISO: end.toISOString(),
        rangeLabel: preset?.label ?? '3d'
    };
  }, [rangeKey, launchDate]);

    useEffect(() => {
      rangeKeyRef.current = rangeKey;
      setArrowResolutionMin(bucketMinutesForRange(rangeKey));
    }, [rangeKey]);

    // --- FETCH INSTALLATION DATE (New Separate Effect) ---
    useEffect(() => {
      let cancelled = false;
      const fetchLaunchDate = async () => {
        // Only run if we have a grid, a token, and haven't found the date yet
        if (!gridData || !token || launchDate) return;

        try {
          // The mooring's moored_at is the date it was deployed to *this* grid —
          // exactly what "Since Launch" needs. (getMooringInstallations is not
          // used here: that endpoint ignores the mooring filter and returns
          // every installation in the system.)
          const mRes = await getMooringsByGridId(gridId, token);
          const moorings = mRes.results || mRes;
          if (!moorings?.length || cancelled) return;
          if (moorings[0].moored_at) setLaunchDate(moorings[0].moored_at);
        } catch (err) {
          // We log this silently so it doesn't crash the main dashboard with a Red Alert
          console.warn("Background fetch of launch date failed:", err);
        }
      };

      fetchLaunchDate();
      return () => { cancelled = true; };
    }, [gridId, gridData, token, launchDate]); // Dependencies are now correct
    // --- DATA FETCHING ---
    useEffect(() => {
        let cancelled = false;
        const KINDS_P1 = ['ph_25', 'o2_25', 'o2', 'ph', 'humidity_o2', 'battery_voltage_calibrated', 'turbidity', 'temperature', 'chla', 'turbidity', 'cdom', 'conductivity', 'humidity_o2', 'par'];
        const KINDS_P2 = ['wind_speed', 'wind_direction_true', 'wind_speed_ext', 'wind_direction_ext', 'precip_accum_1hr_ext', 'precip_accum_24hr_ext', 'air_temperature_ext'];
        const KINDS_P3 = ['wave_height', 'wave_direction', 'wave_direction_true'];

        // Move mergePayloads definition here or outside
        const mergePayloads = (a, b) => {
            const safeA = Array.isArray(a) ? a : [];
            const safeB = Array.isArray(b) ? b : [];
            const seen = new Set(safeA.map(r => `${r.mooring}|${r.time}|${r.sensor_kind}|${r.depth}`));
            return [...safeA, ...safeB.filter(r => !seen.has(`${r.mooring}|${r.time}|${r.sensor_kind}|${r.depth}`))];
        };

        const run = async () => {
            setPrefetching(false);
            // if (!token || !gridData || gridsLoading) return;

            if (rangeKey === '1m' && oneMonthCache) {
                setWaterQualityData(oneMonthCache);
                setLatestLatLon(pickLatestLatLonFromPayload(oneMonthCache));
                return;
            }

            try {
                setLoading(true); setError(null);
                const mRes = await getMooringsByGridId(gridId, token);
                const moorings = mRes.results || mRes;
                const mooring = moorings.find(m => m.id === Number(mooringId));
                setMooring(mooring);
                if (!moorings?.length || !mooring) throw new Error('No moorings found');

                // console.log("mooring: ", mooring);
                // console.log("mooring_status: ", mooring.status);
                if (mooring.status?.latest_sync) setLatestSync(mooring.status.latest_sync);

                const intervalMin = bucketMinutesForRange(rangeKey);
                const agg = 'mean';

                // Fetch Priority 1
                const p1Response = await getMooringCleanedRollups(mooringId, { kinds: KINDS_P1, startDate: startDateISO, endDate: endDateISO, intervalMin, agg }, token);
                // SAFETY CHECK: Ensure response is an array
                const p1 = Array.isArray(p1Response) ? p1Response : [];

                if (cancelled) return;
                setWaterQualityData(p1);
                setLatestLatLon(pickLatestLatLonFromPayload(p1));
                setLoading(false);
                setLoadingPriority2(true); setLoadingPriority3(true);

                // Fetch Priority 2 & 3
                const [p2Res, p3Res] = await Promise.all([
                    getMooringCleanedRollups(mooringId, { kinds: KINDS_P2, startDate: startDateISO, endDate: endDateISO, intervalMin, agg }, token),
                    getMooringCleanedRollups(mooringId, { kinds: KINDS_P3, startDate: startDateISO, endDate: endDateISO, intervalMin, agg }, token)
                ]);

                // SAFETY CHECKS
                const p2 = Array.isArray(p2Res) ? p2Res : [];
                const p3 = Array.isArray(p3Res) ? p3Res : [];

                if (cancelled) return;
                setWaterQualityData(prev => {
                    const merged = mergePayloads(prev, mergePayloads(p2, p3));
                    setLatestLatLon(pickLatestLatLonFromPayload(merged));
                    return merged;
                });
                setLoadingPriority2(false); setLoadingPriority3(false);

                // Prefetch logic (unchanged, just keep checking cancelled)
                if (rangeKey !== 'custom' && rangeKey !== 'launch' && !oneMonthCache) {
                    // ... (existing prefetch logic)
                }
            } catch (e) {
                if (!cancelled) {
                    console.error("Fetch error:", e);
                    setError(e.message || "Failed to load data");
                    setLoading(false);
                    setLoadingPriority2(false);
                    setLoadingPriority3(false);
                }
            }
        };
        run();
        return () => { cancelled = true; };
      }, [gridId, gridData, token, gridsLoading, startDateISO, endDateISO, rangeKey, oneMonthCache, launchDate]);


    // ==========================================
    // 4. MEMOIZED DATA TRANSFORMATIONS
    // ==========================================

    const transformedData = useMemo(() => transformSensorData(waterQualityData), [waterQualityData]);
    const availableSensorTypes = useMemo(() => Object.keys(SENSOR_TYPES).filter(t => transformedData[t]?.length), [transformedData]);
    const availableMetTypes = useMemo(() => MET_CHOICES.filter(t => transformedData[t]?.length), [transformedData]);
    const { haveInternalWind, haveExternalWind } = useMemo(() => ({
        haveInternalWind: (transformedData.wind_direction_true?.length && transformedData.wind_speed?.length),
        haveExternalWind: (transformedData.wind_direction_ext?.length && transformedData.wind_speed_ext?.length)
    }), [transformedData]);

    useEffect(() => {
      if (windCardSource === 'internal' && !haveInternalWind && haveExternalWind) setWindCardSource('external');
      else if (windCardSource === 'external' && !haveExternalWind && haveInternalWind) setWindCardSource('internal');
    }, [windCardSource, haveInternalWind, haveExternalWind]);

    const currentIndicators = useMemo(() => {
      if (!Object.keys(transformedData).length) return { phycocyanin: { value: 0, unit: 'μg/L' }, chlorophyll: { value: 0, unit: 'μg/L' }, temperature: { value: 0, unit: '°F' }, oxygen_25: { value: 0, unit: 'mg/L' }, oxygen: { value: 0, unit: 'mg/L' }, cyanobacteriaIndex: 0 };

      const sixHr = new Date(Date.now() - 6 * 3600000);
      const avg = (key) => {
          const arr = transformedData[key]?.filter(r => new Date(r.time) >= sixHr) || [];
          return arr.length ? arr.reduce((s, r) => s + r.sensor_value, 0) / arr.length : 0;
      };
      const tempC = transformedData.temperature?.[0]?.sensor_value;

      return {
          phycocyanin: { value: avg('pc'), unit: 'μg/L' },
          chlorophyll: { value: avg('chla'), unit: 'μg/L' },
          temperature: { value: tempC ? (tempC * 9/5) + 32 : 0, unit: '°F' },
          oxygen: { value: (transformedData.o2?.[0]?.sensor_value || 0) * 0.032, unit: 'mg/L' },
          oxygen_25: { value: (transformedData.o2_25?.[0]?.sensor_value || 0) * 0.032, unit: 'mg/L' },
          cyanobacteriaIndex: transformedData.cyanobacteria_index?.[0]?.sensor_value || 0
      };
    }, [transformedData]);

    const cyanoChartData = useMemo(() => generateChartData(transformedData, 'cyanobacteria_index'), [transformedData]);
    const cyanoPlotData = useMemo(() => generatePlotData(cyanoChartData, 'cyanobacteria_index'), [cyanoChartData]);

    const timeseriesData = useMemo(() => generateChartData(transformedData, selectedTimeseriesType), [transformedData, selectedTimeseriesType]);
    const timeseriesPlotData = useMemo(() => generatePlotData(timeseriesData, selectedTimeseriesType), [timeseriesData, selectedTimeseriesType]);

    const envData = useMemo(() => generateChartData(transformedData, selectedEnvType), [transformedData, selectedEnvType]);
    const envPlotData = useMemo(() => generatePlotData(envData, selectedEnvType), [envData, selectedEnvType]);

    const metVectorData = useMemo(() => {
      if (['wind_direction_true', 'wind_direction_ext'].includes(selectedMetType)) {
          const isExt = selectedMetType === 'wind_direction_ext';
          return buildArrowBaselineTimeSeries(transformedData,
              isExt ? 'wind_direction_ext' : 'wind_direction_true',
              isExt ? 'wind_speed_ext' : 'wind_speed',
              { resolutionMin: arrowResolutionMin, applyCalm: true });
      }
      return null;
    }, [transformedData, selectedMetType, arrowResolutionMin]);

    const metLineData = useMemo(() => {
        if (!metVectorData) {
            const raw = generateChartData(transformedData, selectedMetType);
            return generatePlotData(raw, selectedMetType);
        }
        return null;
    }, [transformedData, selectedMetType, metVectorData]);

    const waveVectorData = useMemo(() => {
        if (['wind_direction_true', 'wave_direction'].includes(selectedEnvType)) {
            const isWind = selectedEnvType === 'wind_direction_true';
            return buildArrowBaselineTimeSeries(transformedData,
                isWind ? 'wind_direction_true' : 'wave_direction',
                isWind ? 'wind_speed' : 'wave_height',
                { resolutionMin: arrowResolutionMin, applyCalm: isWind });
        }
        return null;
    }, [transformedData, selectedEnvType, arrowResolutionMin]);

    const compassRoseData = useMemo(() => {
        if (!isAdmin || loadingPriority2) return null;
        const wDir = windRoseSource === 'external' ? 'wind_direction_ext' : 'wind_direction_true';
        const waveDir = (transformedData.wave_direction?.length) ? 'wave_direction' : 'wave_direction_true';
        return {
            wind: buildDirectionRose(transformedData, { dirKind: wDir }),
            wave: buildDirectionRose(transformedData, { dirKind: waveDir }),
            wDirTitle: windRoseSource === 'external' ? 'Wind Direction (ext)' : 'Wind Direction'
        };
    }, [transformedData, windRoseSource, isAdmin, loadingPriority2]);

    const onRelayout = useCallback((ev) => {
        const x0 = ev?.['xaxis.range[0]'] || ev?.['xaxis.range']?.[0];
        const x1 = ev?.['xaxis.range[1]'] || ev?.['xaxis.range']?.[1];
        if (x0 && x1) {
            const ms = new Date(x1) - new Date(x0);
            setArrowResolutionMin(prev => {
                const next = computeArrowResolution(ms);
                const cap = bucketMinutesForRange(rangeKeyRef.current);
                return Math.min(next, cap);
            });
        }
    }, []);

    // --- RENDER ---
    if (gridsLoading || loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress /> <Typography ml={2}>Loading pond data...</Typography></Box>;
    if (error) return <Alert severity="error"><AlertTitle>Error</AlertTitle>{error}</Alert>;
    if (!gridData) return <Alert severity="info">Grid Not Found</Alert>;

    return (
      <div className="water-quality-dashboard">


        <Box className="status-card-container">
          <CardContent>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h5">
                Buoy: {mooring?.buoy && `${mooring.buoy}`}
            </Typography>
                  <Box display="flex" alignItems="center">
                        Current Status:
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getStatusDotColor(latestSync), mr: 1 }} />
                      <Typography variant="body2">Updated: {formatDateTime(latestSync)}</Typography>
                  </Box>
              </Box>
          </CardContent>
        </Box>

  <Card className="dashboard-header-card" sx={{ mt: 2 }}>
          <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">Range: {rangeLabel}</Typography>
                  {rangeKey === 'launch' && launchDate && (
                      <Typography variant="body1" color="text.secondary">
                          ({new Date(launchDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                      </Typography>
                  )}
                  {prefetching && <CircularProgress size={14} />}
              </Box>
              <ButtonGroup size="small">
                  {TIME_RANGES.map(r => (
                      <Button
                        key={r.key}
                        onClick={() => setRangeKey(r.key)}
                        variant={rangeKey === r.key ? 'contained' : 'outlined'}
                      >
                        {r.label}
                      </Button>
                  ))}
              </ButtonGroup>
          </CardContent>
        </Card>

        <Card className="timeseries-chart-card">
          <CardContent>
              <Box className="timeseries-chart-header" sx={{ mb: 1, gap: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h5" component="h2" className="timeseries-chart-title">
                      Water Quality Trends (Past {rangeLabel})
                  </Typography>
              </Box>

              <Box className="chart-toggle-container" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['temperature','conductivity', 'o2_25',  'ph_25', 'o2', 'ph' , 'chla', 'turbidity', 'cdom', 'humidity_o2', 'battery_voltage_calibrated', 'pump_speed', 'par']
                      .filter((t) => availableSensorTypes.includes(t))
                      .map((sensorType) => (
                          <Button
                              key={sensorType}
                              onClick={() => setSelectedTimeseriesType(sensorType)}
                              className={`chart-toggle-button ${selectedTimeseriesType === sensorType ? 'active' : ''}`}
                              variant={selectedTimeseriesType === sensorType ? 'contained' : 'outlined'}
                          >
                              {SENSOR_TYPES[sensorType].name}
                          </Button>
                      ))}
              </Box>

              {timeseriesData.length === 0 ? (
                  <Box className="no-timeseries-data" sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: 1 }}>
                      <Typography variant="body1" color="textSecondary">
                          No {SENSOR_TYPES[selectedTimeseriesType]?.name.toLowerCase()} data available for the selected period.
                      </Typography>
                  </Box>
              ) : (
                  <Plot
                      data={timeseriesPlotData}
                      layout={{
                          height: 350,
                          margin: { l: 20, r: 20, t: 20, b: 60 },
                          xaxis: { type: 'date', tickformat: '%b %d', automargin: true },
                          yaxis: {
                              title: { text: `${SENSOR_TYPES[selectedTimeseriesType].name} (${SENSOR_TYPES[selectedTimeseriesType].unit})` },
                              automargin: true
                          },
                          showlegend: true,
                          legend: { orientation: 'h', x: 0.5, xanchor: 'center', y: -0.2 },
                          paper_bgcolor: 'rgba(0,0,0,0)',
                          plot_bgcolor: 'rgba(0,0,0,0)',
                          hovermode: 'x unified'
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                  />
              )}
          </CardContent>
        </Card>
        {isAdmin && (
        <Card className="timeseries-chart-card">
          <CardContent>
              <Box className="timeseries-chart-header" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h5" className="timeseries-chart-title">
                      Meteorological Trends (Past {rangeLabel})
                  </Typography>
                  {loadingPriority2 && <CircularProgress size={16} sx={{ ml: 2 }} />}
              </Box>

              <Box className="chart-toggle-container" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {availableMetTypes.map(t => (
                      <Button
                          key={t}
                          onClick={() => setSelectedMetType(t)}
                          className={`chart-toggle-button ${selectedMetType === t ? 'active' : ''}`}
                          variant={selectedMetType === t ? 'contained' : 'outlined'}
                      >
                          {SENSOR_TYPES[t].name}
                      </Button>
                  ))}
              </Box>

              <Box height={350}>
                  {metVectorData ? (
                      <Plot
                          data={metVectorData.traces}
                          layout={{
                              height: 320,
                              xaxis: { type: 'date' },
                              yaxis: { showticklabels: false, range: [0, 2], fixedrange: true },
                              annotations: [...metVectorData.annotations, ...buildCompassRose('tr').annotations],
                              shapes: [...buildCompassRose('tr').shapes, ...(metVectorData.baselineShape ? [metVectorData.baselineShape] : [])],
                              margin: { l: 40, r: 40, t: 20, b: 80 },
                              showlegend: false,
                              hovermode: 'closest',
                              paper_bgcolor: 'rgba(0,0,0,0)',
                              plot_bgcolor: 'rgba(0,0,0,0)'
                          }}
                          onRelayout={onRelayout}
                          config={{ displayModeBar: false, responsive: true }}
                          style={{ width: '100%' }}
                      />
                  ) : (
                      <Plot
                          data={metLineData}
                          layout={{
                              height: 350,
                              margin: { b: 80 },
                              xaxis: { type: 'date' },
                              yaxis: {
                                  title: { text: `${SENSOR_TYPES[selectedMetType].name} (${SENSOR_TYPES[selectedMetType].unit})` },
                                  automargin: true
                              },
                              paper_bgcolor: 'rgba(0,0,0,0)',
                              plot_bgcolor: 'rgba(0,0,0,0)',
                              hovermode: 'x unified'
                          }}
                          config={{ displayModeBar: false, responsive: true }}
                          style={{ width: '100%' }}
                      />
                  )}
              </Box>
              {selectedMetType.endsWith('_ext') && (
                  <Typography variant="caption" sx={{ display:'block', textAlign:'right', mt:1, color:'#546e7a' }}>
                      • Any variable with an <strong>ext</strong> extension indicates these are sourced from external weather stations.
                  </Typography>
              )}
          </CardContent>
        </Card>
        )}

        {isAdmin && (
            <Card className="timeseries-chart-card">
              <CardContent>
                  <Box className="timeseries-chart-header" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h5" className="timeseries-chart-title">
                          Wave Trends (Past {rangeLabel})
                      </Typography>
                      {loadingPriority3 && <CircularProgress size={16} sx={{ ml: 2 }} />}
                  </Box>

                  <Box className="chart-toggle-container" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {['wind_speed', 'wind_direction_true', 'wave_height', 'wave_direction']
                          .filter(t => availableSensorTypes.includes(t))
                          .map(t => (
                              <Button
                                  key={t}
                                  onClick={() => setSelectedEnvType(t)}
                                  className={`chart-toggle-button ${selectedEnvType === t ? 'active' : ''}`}
                                  variant={selectedEnvType === t ? 'contained' : 'outlined'}
                              >
                                  {SENSOR_TYPES[t].name}
                              </Button>
                      ))}
                  </Box>

                  <Box height={350}>
                      {waveVectorData ? (
                          <Plot
                              data={waveVectorData.traces}
                              layout={{
                                  height: 320,
                                  xaxis: { type: 'date' },
                                  yaxis: { showticklabels: false, range: [0, 2], fixedrange: true },
                                  annotations: [...waveVectorData.annotations, ...buildCompassRose('tr').annotations],
                                  shapes: [...buildCompassRose('tr').shapes],
                                  margin: { l: 40, r: 40, t: 20, b: 80 },
                                  showlegend: false,
                                  hovermode: 'closest',
                                  paper_bgcolor: 'rgba(0,0,0,0)',
                                  plot_bgcolor: 'rgba(0,0,0,0)'
                              }}
                              onRelayout={onRelayout}
                              config={{ displayModeBar: false, responsive: true }}
                              style={{ width: '100%' }}
                          />
                      ) : (
                          <Plot
                              data={envPlotData}
                              layout={{
                                  height: 350,
                                  margin: { b: 80 },
                                  xaxis: { type: 'date' },
                                  yaxis: {
                                      title: { text: `${SENSOR_TYPES[selectedEnvType].name} (${SENSOR_TYPES[selectedEnvType].unit})` },
                                      automargin: true
                                  },
                                  paper_bgcolor: 'rgba(0,0,0,0)',
                                  plot_bgcolor: 'rgba(0,0,0,0)',
                                  hovermode: 'x unified'
                              }}
                              config={{ displayModeBar: false, responsive: true }}
                              style={{ width: '100%' }}
                          />
                      )}
                  </Box>
              </CardContent>
            </Card>
        )}

        {isAdmin && compassRoseData && (
            <Card className="timeseries-chart-card">
                <CardContent>
                    {/* Header Row */}
                    <Box className="timeseries-chart-header" sx={{ mb: 1 }}>
                        <Typography variant="h5" className="timeseries-chart-title">
                            Direction Roses (Past {rangeLabel})
                        </Typography>
                    </Box>

                    {/* Button Row */}
                    <Box className="chart-toggle-container" sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            onClick={() => setWindRoseSource('internal')}
                            variant={windRoseSource === 'internal' ? 'contained' : 'outlined'}
                            className={`chart-toggle-button ${windRoseSource === 'internal' ? 'active' : ''}`}
                        >
                            Buoy
                        </Button>
                        <Button
                            onClick={() => setWindRoseSource('external')}
                            variant={windRoseSource === 'external' ? 'contained' : 'outlined'}
                            className={`chart-toggle-button ${windRoseSource === 'external' ? 'active' : ''}`}
                        >
                            External (Ext)
                        </Button>
                    </Box>

                    {/* Charts Grid */}
                    <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={2}>
                        {[
                            { title: compassRoseData.wDirTitle, data: compassRoseData.wind },
                            { title: 'Wave Direction', data: compassRoseData.wave }
                        ].map((rose, i) => (
                            <Paper key={i} elevation={0} variant="outlined" sx={{ p: 2 }}>
                                <Typography align="center" variant="subtitle2" gutterBottom>{rose.title}</Typography>
                                {rose.data.theta.length ? (
                                    <Plot
                                        data={[{ type: 'barpolar', theta: rose.data.theta, r: rose.data.r, marker: {line:{width:1}} }]}
                                        layout={{
                                            height: 300,
                                            margin: { l: 30, r: 30, t: 20, b: 20 },
                                            showlegend: false,
                                            paper_bgcolor: 'rgba(0,0,0,0)',
                                            plot_bgcolor: 'rgba(0,0,0,0)',
                                            polar: {
                                              barmode: 'overlay',
                                              angularaxis: {
                                                  direction: 'clockwise',
                                                  rotation: 90,
                                                  tickmode: 'array',
                                                  tickvals: ROSE_TICKS.vals,
                                                  ticktext: ROSE_TICKS.text
                                              },
                                              radialaxis: { showticklabels: false, ticks: '' }
                                            }
                                        }}
                                        config={{ displayModeBar: false }}
                                        style={{ width: '100%' }}
                                    />
                                ) : <Typography align="center" color="textSecondary">No Data</Typography>}
                            </Paper>
                        ))}
                    </Box>
                    <Box sx={{ mt: 1.5, textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: '#546e7a', display: 'block' }}>
                          • Rose plots use the <strong>FROM</strong> convention (meteorological standard).
                        </Typography>
                        {windRoseSource === 'external' && (
                            <Typography variant="caption" sx={{ color: '#546e7a', display: 'block', mt: 0.5 }}>
                              • Any variable with an <strong>ext</strong> extension indicates these are sourced from external weather stations.
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        )}



        <Box className="dashboard-footer" mt={4} textAlign="center">
            <Typography variant="body2" color="textSecondary">© 2026 Subtidal, Inc. All rights reserved.</Typography>
        </Box>

        <SafetyLevelsDialog open={safetyDialogOpen} onClose={() => setSafetyDialogOpen(false)} />
      </div>
    );
  }